import {
  type ContractWithRenewal,
  type RenewalAnswer,
  useAnswerRenewal
} from '@services/contracts/mutations/useAnswerRenewal'
import { format, parseISO } from 'date-fns'
import { CalendarClock, CircleCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'

/**
 * "Your lease ends on X - do you want to stay?" for the main tenant, in the
 * last 75 days of a running lease. The answer is only recorded; SNA arranges
 * the extension or the handover. It can be changed until the lease is settled.
 */
export function RenewalCard({ contract }: { contract?: ContractWithRenewal }) {
  const { t } = useTranslation('contract')
  const renewal = contract?.renewal
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(renewal?.note ?? '')
  const { mutate, isPending } = useAnswerRenewal()

  if (!contract?.id || !renewal?.can_answer || !contract.end_date) return null

  const endDate = format(parseISO(contract.end_date), 'dd.MM.yyyy')
  const showForm = editing || !renewal.answer

  const submit = (answer: RenewalAnswer) =>
    mutate(
      { id: contract.id as string, answer, note },
      { onSuccess: () => setEditing(false) }
    )

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CalendarClock className="h-5 w-5" />
        </span>
        <span className="font-display text-base font-bold text-ink">
          {t('renewal.title', { date: endDate })}
        </span>
      </div>

      {showForm ? (
        <>
          <p className="m-0 text-sm leading-relaxed text-muted-foreground">
            {t('renewal.question')}
          </p>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('renewal.note_placeholder')}
            maxLength={1000}
            rows={2}
          />
          <div className="flex flex-wrap gap-3">
            <Button disabled={isPending} onClick={() => submit('EXTEND')}>
              {t('renewal.extend')}
            </Button>
            <Button
              variant="outline"
              disabled={isPending}
              onClick={() => submit('NOT_EXTEND')}
            >
              {t('renewal.not_extend')}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink">
            <CircleCheck className="h-4 w-4 text-primary" />
            {renewal.answer === 'EXTEND'
              ? t('renewal.answered_extend')
              : t('renewal.answered_not_extend')}
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t('renewal.change')}
          </Button>
          <p className="m-0 w-full text-sm text-muted-foreground">
            {t('renewal.next_steps')}
          </p>
        </div>
      )}
    </Card>
  )
}
